import React, { useEffect, useState, useCallback } from 'react';
import { usePage } from '../../hooks/usePage';
import {
  paymentRequestsApi,
  AdminPaymentRequest,
  AdminPaymentRequestDetail,
  PaymentRequestStatus,
  PaymentRequestType,
  PaymentRequestDelivery,
} from '../../api/requests';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';
import { Drawer } from '../../components/ui/Drawer';
import { RequestDetailDrawer } from './RequestDetailDrawer';

const STATUS_OPTIONS: PaymentRequestStatus[] = [
  'open',
  'partly_paid',
  'settled',
  'expired',
  'cancelled',
];

const statusColors: Record<
  PaymentRequestStatus,
  'success' | 'warning' | 'danger' | 'neutral' | 'info'
> = {
  open: 'info',
  partly_paid: 'warning',
  settled: 'success',
  expired: 'neutral',
  cancelled: 'danger',
};

const formatNaira = (n: number) =>
  `₦${n.toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;

const formatDate = (s: string) =>
  new Date(s).toLocaleDateString('en-NG', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

// Compact per-request WhatsApp delivery summary.
const DeliveryChips: React.FC<{
  delivery: PaymentRequestDelivery;
  remindCount: number;
}> = ({ delivery, remindCount }) => {
  if (remindCount === 0) {
    return <span style={{ color: 'var(--neutral-500)', fontSize: 12 }}>Not reminded</span>;
  }
  const chips: Array<{ label: string; variant: 'success' | 'info' | 'neutral' | 'danger' }> = [];
  if (delivery.read) chips.push({ label: `${delivery.read} read`, variant: 'success' });
  if (delivery.delivered) chips.push({ label: `${delivery.delivered} delivered`, variant: 'info' });
  if (delivery.sent) chips.push({ label: `${delivery.sent} sent`, variant: 'neutral' });
  if (delivery.failed) chips.push({ label: `${delivery.failed} failed`, variant: 'danger' });
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
      {chips.length ? (
        chips.map((c, i) => (
          <Badge key={i} variant={c.variant}>
            {c.label}
          </Badge>
        ))
      ) : (
        <span style={{ color: 'var(--neutral-500)', fontSize: 12 }}>Email only</span>
      )}
      <span style={{ color: 'var(--neutral-500)', fontSize: 11 }}>
        {remindCount}× reminded
      </span>
    </div>
  );
};

export const Requests: React.FC = () => {
  usePage('Payment Requests');

  const [data, setData] = useState<AdminPaymentRequest[]>([]);
  const [meta, setMeta] = useState<{ page: number; totalPages: number } | null>(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<PaymentRequestStatus | ''>('');
  const [type, setType] = useState<PaymentRequestType | ''>('');
  const [reminder, setReminder] = useState<'' | 'reminded' | 'not_reminded' | 'has_failed'>('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<AdminPaymentRequestDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = useCallback(async (id: string) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await paymentRequestsApi.get(id));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const result = await paymentRequestsApi.list({
        page,
        limit: 20,
        status: status || undefined,
        type: type || undefined,
        reminder: reminder || undefined,
        search: search.trim() || undefined,
      });
      setData(result.items);
      setMeta({
        page: result.page,
        totalPages: Math.max(1, Math.ceil(result.total / result.limit)),
      });
    } finally {
      setLoading(false);
    }
  }, [page, status, type, reminder, search]);

  useEffect(() => {
    void load();
  }, [load]);

  // Reset to page 1 whenever a filter changes.
  useEffect(() => {
    setPage(1);
  }, [status, type, reminder, search]);

  const filterSelectStyle: React.CSSProperties = {
    height: 38,
    padding: '0 32px 0 12px',
    fontSize: 13,
    fontFamily: 'var(--font-family)',
    color: 'var(--neutral-700)',
    background: 'var(--neutral-100)',
    border: '1px solid var(--neutral-300)',
    borderRadius: 'var(--radius-md)',
    outline: 'none',
    cursor: 'pointer',
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div style={{ minWidth: 220, flex: '1 1 220px' }}>
          <Input
            placeholder="Search by title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as PaymentRequestStatus | '')}
          style={filterSelectStyle}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </option>
          ))}
        </select>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as PaymentRequestType | '')}
          style={filterSelectStyle}
        >
          <option value="">All types</option>
          <option value="single">Single</option>
          <option value="group">Group</option>
        </select>
        <select
          value={reminder}
          onChange={(e) =>
            setReminder(e.target.value as '' | 'reminded' | 'not_reminded' | 'has_failed')
          }
          style={filterSelectStyle}
        >
          <option value="">Any reminder state</option>
          <option value="reminded">Reminded</option>
          <option value="not_reminded">Not reminded</option>
          <option value="has_failed">Has failed delivery</option>
        </select>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner size={28} />
        </div>
      ) : (
        <Card padding={0}>
          <Table
            columns={[
              {
                key: 'title',
                header: 'Request',
                render: (r: AdminPaymentRequest) => (
                  <div>
                    <div style={{ color: 'var(--neutral-800)', fontWeight: 600 }}>
                      {r.title}
                    </div>
                    <div style={{ color: 'var(--neutral-500)', fontSize: 12 }}>
                      {r.creator?.name ?? 'Unknown'}
                      {r.creator?.email ? ` · ${r.creator.email}` : ''}
                    </div>
                  </div>
                ),
              },
              {
                key: 'type',
                header: 'Type',
                render: (r: AdminPaymentRequest) => (
                  <span style={{ textTransform: 'capitalize' }}>{r.type}</span>
                ),
              },
              {
                key: 'status',
                header: 'Status',
                render: (r: AdminPaymentRequest) => (
                  <Badge variant={statusColors[r.status]}>
                    {r.status.replace('_', ' ')}
                  </Badge>
                ),
              },
              {
                key: 'amount',
                header: 'Collected',
                render: (r: AdminPaymentRequest) => (
                  <div>
                    <div style={{ color: 'var(--neutral-800)' }}>
                      {formatNaira(r.paidAmount)} / {formatNaira(r.totalAmount)}
                    </div>
                    <div style={{ color: 'var(--neutral-500)', fontSize: 12 }}>
                      {r.paidCount}/{r.lineCount} paid
                    </div>
                  </div>
                ),
              },
              {
                key: 'reminders',
                header: 'Reminders',
                render: (r: AdminPaymentRequest) => (
                  <DeliveryChips delivery={r.delivery} remindCount={r.remindCount} />
                ),
              },
              {
                key: 'createdAt',
                header: 'Created',
                render: (r: AdminPaymentRequest) => formatDate(r.createdAt),
              },
              {
                key: 'action',
                header: '',
                render: (r: AdminPaymentRequest) => (
                  <Button size="sm" variant="secondary" onClick={() => openDetail(r.id)}>
                    View
                  </Button>
                ),
              },
            ]}
            data={data}
            emptyMessage="No payment requests match these filters"
          />
          {meta && meta.totalPages > 1 && (
            <div style={{ padding: 16 }}>
              <Pagination
                page={meta.page}
                totalPages={meta.totalPages}
                total={0}
                onPageChange={setPage}
              />
            </div>
          )}
        </Card>
      )}

      <Drawer
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={detail?.title ?? 'Payment request'}
        subtitle={detail?.creator?.name ? `by ${detail.creator.name}` : undefined}
        width={560}
      >
        {detailLoading || !detail ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={26} />
          </div>
        ) : (
          <RequestDetailDrawer detail={detail} />
        )}
      </Drawer>
    </>
  );
};
